import { Profile } from '../types';

const getTimestamp = (p?: Profile): number => {
    if (!p?.updatedAt) return 0;
    const t = Date.parse(p.updatedAt);
    return isNaN(t) ? 0 : t;
};

/**
 * Fusionador de perfiles y permisos globales.
 * 
 * Reglas estrictas (Regla 9 AGENTS.md):
 * 1. NUNCA sobrescribir perfiles del servidor con plantillas por defecto o listas vacías locales.
 * 2. Si el servidor tiene perfiles (remotos), cualquier cambio local debe respetar y preservar
 *    los perfiles creados por los administradores y sus permisos configurados.
 * 3. Si un perfil ya existe en el servidor, no permitir que un cliente con estado desactualizado
 *    elimine o degrade permisos que el administrador haya activado.
 * 4. Si hay conflicto entre local y remoto, se respeta la versión que tenga la edición manual más
 *    reciente (`updatedAt`). Si ninguna tiene `updatedAt` (perfiles antiguos o plantillas por defecto),
 *    la versión del servidor SIEMPRE tiene prioridad sobre la local para evitar que plantillas de código
 *    reviertan los cambios realizados por el administrador.
 * 5. Nuevos perfiles añadidos localmente se integran sin borrar perfiles remotos.
 */
export function mergeGlobalProfiles(local: Profile[], remote: Profile[]): Profile[] {
    if (!Array.isArray(remote) || remote.length === 0) {
        return Array.isArray(local) ? local : [];
    }

    if (!Array.isArray(local) || local.length === 0) {
        // El cliente local no tiene perfiles cargados (o está vacío/reiniciando).
        // NUNCA borrar los perfiles del servidor.
        return remote;
    }

    const localMap = new Map<string, Profile>();
    local.forEach(p => {
        if (p && p.id) localMap.set(p.id, p);
    });

    // Empezamos con la base de perfiles remotos existentes en el servidor
    const mergedList: Profile[] = remote.map(remoteProf => {
        const localProf = localMap.get(remoteProf.id);
        if (!localProf) {
            // El cliente local no lo tenía; conservamos el perfil del servidor intacto
            return remoteProf;
        }

        const localTs = getTimestamp(localProf);
        const remoteTs = getTimestamp(remoteProf);

        // Si el cliente local tiene un cambio deliberado más reciente que el remoto:
        if (localTs > remoteTs) {
            return localProf;
        }

        // Si el remoto es más reciente, o tienen igual timestamp (o ninguno tiene timestamp):
        // La versión del servidor (remota) es la FUENTE DE VERDAD.
        // Pero aseguramos que no falte ninguna propiedad funcional:
        const mergedPermissions = {
            ...(localProf.permissions || {}),
            ...(remoteProf.permissions || {})
        };

        return {
            ...remoteProf,
            permissions: mergedPermissions
        };
    });

    // Agregar perfiles nuevos que se hayan creado en local y no existan en remoto
    const remoteIds = new Set(remote.map(p => p.id));
    local.forEach(localProf => {
        if (localProf && localProf.id && !remoteIds.has(localProf.id)) {
            mergedList.push(localProf);
        }
    });

    return mergedList;
}
